/* External dependencies */
import CognitoClient from '@mancho.devs/cognito';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

/* Local dependencies */
import { getSessionPromise } from './getSessionPromise';
import {
  AuthAction,
  ConfirmCode,
  signUpFailed,
  signInFailed,
  SignInRequest,
  signOutFailed,
  SignUpRequest,
  signUpSucceeded,
  signInSucceeded,
  signOutSucceeded,
  getSessionFailed,
  SignUpActionTypes,
  SignInActionTypes,
  GetSessionRequest,
  SignOutActionTypes,
  getSessionSucceeded,
  passwordForgotFailed,
  GetSessionActionTypes,
  confirmPasswordFailed,
  PasswordForgotRequest,
  ConfirmCodeActionTypes,
  ConfirmPasswordRequest,
  signUpConfirmCodeFailed,
  passwordForgotSucceeded,
  confirmPasswordSucceeded,
  PasswordForgotActionTypes,
  ConfirmPasswordActionTypes,
  signUpConfirmCodeSucceeded,
} from './signUp/actions';

const cognitoClient = new CognitoClient({
  UserPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
  ClientId: process.env.GATSBY_COGNITO_CLIENT_ID,
});

export function signUpEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === SignUpActionTypes.SIGNUP),
    switchMap(({ password, email }: SignUpRequest) =>
      cognitoClient.signUp(email, password).then(signUpSucceeded).catch(signUpFailed),
    ),
  );
}

export function confirmCodeEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === ConfirmCodeActionTypes.CONFIRM_CODE),
    switchMap(({ email, code }: ConfirmCode) =>
      cognitoClient.signUpConfirmCode(email, code).then(signUpConfirmCodeSucceeded).catch(signUpConfirmCodeFailed),
    ),
  );
}

export function signInEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === SignInActionTypes.SIGNIN),
    switchMap(({ email, password }: SignInRequest) =>
      cognitoClient.signIn(email, password).then(signInSucceeded).catch(signInFailed),
    ),
  );
}

export function signOutEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === SignOutActionTypes.SIGNOUT),
    switchMap(() => cognitoClient.signOut().then(signOutSucceeded).catch(signOutFailed)),
  );
}

export function passwordForgotEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === PasswordForgotActionTypes.PASSWORD_FORGOT),
    switchMap(({ email }: PasswordForgotRequest) =>
      cognitoClient.forgotPassword(email).then(passwordForgotSucceeded).catch(passwordForgotFailed),
    ),
  );
}

export function confirmPasswordEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === ConfirmPasswordActionTypes.PASSWORD_CONFIRM),
    switchMap(({ email, pinCode, newPassword }: ConfirmPasswordRequest) =>
      cognitoClient
        .confirmPassword(email, pinCode, newPassword)
        .then(confirmPasswordSucceeded)
        .catch(confirmPasswordFailed),
    ),
  );
}

export function getSessionEpic(action$): Observable<AuthAction> {
  return action$.pipe(
    filter((action: AuthAction) => action.type === GetSessionActionTypes.GET_SESSION),
    switchMap(({}: GetSessionRequest) =>
      from(getSessionPromise()).pipe(
        map((session) => {
          const userSession = session;
          const email = session.getIdToken().payload.email;
          return getSessionSucceeded(email, userSession);
        }),
        catchError((error) => {
          console.error(error);
          return of(getSessionFailed(error));
        }),
      ),
    ),
  );
}

